import { ApolloProvider } from '@apollo/client';
import { GQLClient } from '@oolio-group/gql-client';
import React, { useCallback, useState, useEffect } from 'react';
import { StatusBar } from 'react-native';
import { authFlowLink } from './utils/authFlowLink';
import config from './config';
import AppNavigator from './navigator/AppNavigator';
import * as settings from './state/preferences';
import { getApolloCache } from '@oolio-group/client-utils';
import { SessionProvider } from './hooks/useSession';
import { ScaleProvider } from './hooks/useScale';
import {
  Locale,
  LocalizationProvider,
  supportedLocales,
} from '@oolio-group/localization';
import Loader from './component/Loader/Loader';
import { ThemeProvider } from './hooks/useColors';
import { App as AppType } from '@oolio-group/domain';
import AnalyticsInitilizer from './analytics/AnalyticsInitilizer';
import BackgroundWorker from './workers/BackgroundWorker';
import { workerInstanceVar } from './state/apolloVars';
import Sentry from './utils/Sentry/Sentry';
import * as storage from './storage/interface';

// TODO: Add localization in KDS
const App = () => {
  const [locale, setLocale] = useState<Locale>();
  const [client] = useState(() => {
    const cache = getApolloCache();

    const getSession = async () => {
      const { kitchenDisplay, currentStore, token } =
        (await settings.getSession()) as settings.Session;
      return {
        device: kitchenDisplay,
        currentStore: currentStore,
        token: token,
        activeApp: AppType.KITCHEN_DISPLAY_APP,
      };
    };

    return new GQLClient(
      {
        cache,
        middlewares: authFlowLink,
        service: config.SERVICE_URI,
        getSession,
        client: 'kds-app',
        version: '1.0',
      },
      true,
    ).getApolloClient();
  });

  const onSessionChange = useCallback(
    (session: settings.Session | undefined) => {
      if (!session) return;
      Sentry.setTags({
        organizationName: session.currentOrganization?.name,
        organization: session.currentOrganization?.id,
        venue: session.currentVenue?.id,
        store: session.currentStore?.id,
        device: session.kitchenDisplay?.id ?? session.device?.id,
        app: AppType.KITCHEN_DISPLAY_APP,
        appVersion: session.device?.appVersion,
      });
    },
    [],
  );

  useEffect(() => {
    settings.getSession().then(onSessionChange);
    storage.addSubscription(settings.SESSION_KEY, onSessionChange);
    return () => {
      storage.removeSubscription(settings.SESSION_KEY, onSessionChange);
    };
  }, [onSessionChange]);

  useEffect(() => {
    (async () => {
      const session = await settings.getSession();
      setLocale(supportedLocales[session?.localeCode || config.DEFAULT_LOCALE]);
    })();
  }, []);

  useEffect(() => {
    const worker = new BackgroundWorker();
    if (worker.worker) workerInstanceVar(worker);
    return () => {
      worker.worker?.terminate?.();
    };
  }, []);

  return (
    <>
      <StatusBar animated={true} barStyle="light-content" hidden />
      {client && locale ? (
        <ApolloProvider client={client}>
          <SessionProvider>
            <AnalyticsInitilizer>
              <LocalizationProvider
                locale={locale}
                country={config.DEFAULT_COUNTRY_CODE}
              >
                <ThemeProvider>
                  <ScaleProvider>
                    <AppNavigator />
                  </ScaleProvider>
                </ThemeProvider>
              </LocalizationProvider>
            </AnalyticsInitilizer>
          </SessionProvider>
        </ApolloProvider>
      ) : (
        <Loader show={true} />
      )}
    </>
  );
};
export default App;
